import { useState } from 'react';
import { 
  Mail, Phone, Check, MapPin, 
  Sparkles, Gift, Coffee, 
  ShoppingBag, Palette, Book, Music, Utensils,
  ArrowLeft, Shield
} from 'lucide-react';
import { components, gradients } from '../theme/wunderCityTheme';

function SignupOnboarding() {
  const [selectedCategories, setSelectedCategories] = useState([]);

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const categories = [
    { id: 'restaurants', icon: Utensils, label: 'Restaurants' },
    { id: 'coffee', icon: Coffee, label: 'Coffee & Cafes' },
    { id: 'shopping', icon: ShoppingBag, label: 'Shopping' },
    { id: 'arts', icon: Palette, label: 'Arts & Culture' },
    { id: 'books', icon: Book, label: 'Books & Media' },
    { id: 'entertainment', icon: Music, label: 'Entertainment' },
  ];

  return (
    <div className="bg-gray-100 min-h-screen py-6 md:py-12">
      {/* Header */}
      <div className="text-center px-4 md:px-8 pb-8 md:pb-12">
        <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
          Pensacola Wunder City UI Design System
        </h1>
        <p className="text-lg md:text-xl text-gray-600">
          Bringing people together in magical ways
        </p>
      </div>

      {/* Screens */}
      <div className="flex flex-col lg:flex-row gap-8 p-4 md:p-8 items-center lg:justify-center overflow-x-auto">
      {/* Solution 1: Ocean Wave Flow */}
      <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
        <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
          <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
        <div className={components.phoneMockup.outer}>
          <div className={components.phoneMockup.inner}>
            {/* Status Bar */}
            <div className={components.statusBar.container}>
              <span className={components.statusBar.time}>9:41</span>
              <div className={components.statusBar.battery}>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
              </div>
            </div>

            {/* Content */}
            <div className={`pt-11 h-full ${gradients.oceanWave}`}>
              {/* Welcome Screen */}
              <div className="h-full flex flex-col">
                {/* Ocean Wave SVG Background */}
                <div className="absolute top-0 left-0 right-0 h-96 overflow-hidden">
                  <svg className="absolute bottom-0 w-full" viewBox="0 0 375 200" preserveAspectRatio="none">
                    <path d="M0,100 C125,150 250,50 375,100 L375,200 L0,200 Z" fill="#0EA5E9" opacity="0.1"/>
                    <path d="M0,120 C125,170 250,70 375,120 L375,200 L0,200 Z" fill="#0EA5E9" opacity="0.15"/>
                    <path d="M0,140 C125,190 250,90 375,140 L375,200 L0,200 Z" fill="#0EA5E9" opacity="0.2"/>
                  </svg>
                </div>

                <div className="flex-1 px-8 pt-20 relative z-10">
                  <div className="text-center mb-12">
                    <div className="w-20 h-20 bg-gradient-to-br from-sky-400 to-sky-600 rounded-3xl mx-auto mb-6 flex items-center justify-center shadow-lg">
                      <Sparkles className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">Welcome to Wunder City</h1>
                    <p className="text-gray-600 text-lg">Discover & earn rewards across downtown Pensacola</p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <button className={`w-full ${components.button.primary} ${components.button.padding.lg} ${components.button.rounded} ${components.button.font} text-lg shadow-lg flex items-center justify-center gap-3`}>
                      <Mail className="w-5 h-5" />
                      Continue with Email
                    </button>
                    
                    <button className="w-full bg-white text-gray-900 py-4 px-6 rounded-2xl font-semibold text-lg shadow-md border-2 border-gray-100 hover:border-sky-200 transition-all transform hover:scale-[0.98] flex items-center justify-center gap-3">
                      <Phone className="w-5 h-5" />
                      Continue with Phone
                    </button>

                    <div className="relative py-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white text-gray-500">or</span>
                      </div>
                    </div>

                    <button className="w-full bg-gray-900 text-white py-4 px-6 rounded-2xl font-semibold text-lg shadow-lg hover:bg-gray-800 transition-all transform hover:scale-[0.98]">
                      Continue with Apple
                    </button>
                    
                    <button className="w-full bg-white text-gray-900 py-4 px-6 rounded-2xl font-semibold text-lg shadow-md border-2 border-gray-200 hover:border-gray-300 transition-all transform hover:scale-[0.98]">
                      Continue with Google
                    </button>
                  </div>

                  <p className="text-center text-sm text-gray-500">
                    By continuing, you agree to our Terms of Service and Privacy Policy
                  </p>
                </div>
              </div>
            </div>
          </div>
            </div>
          </div>
        </div>
        <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Welcome & Auth</p>
      </div>

      {/* Solution 2: Card Stack Journey */}
      <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
        <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
          <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
        <div className={components.phoneMockup.outer}>
          <div className={components.phoneMockup.inner}>
            {/* Status Bar */}
            <div className={components.statusBar.container}>
              <span className={components.statusBar.time}>9:41</span>
              <div className={components.statusBar.battery}>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
              </div>
            </div>

            {/* Content */}
            <div className="pt-11 h-full bg-gray-50">
              {/* Email Verification Screen */}
              <div className="h-full flex flex-col px-6 pt-8">
                <button className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center mb-8">
                  <ArrowLeft className="w-5 h-5 text-gray-700" />
                </button>

                <div className="flex-1">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify your email</h2>
                    <p className="text-gray-600">We sent a code to john.doe@email.com</p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Verification Code</label>
                      <div className="flex gap-3">
                        {[...Array(6)].map((_, i) => (
                          <input
                            key={i}
                            type="text"
                            maxLength="1"
                            className="w-12 h-14 text-center text-xl font-semibold bg-gray-100 rounded-xl border-2 border-transparent focus:border-sky-500 focus:bg-white transition-all"
                            defaultValue={i < 4 ? [1,2,3,4][i] : ''}
                          />
                        ))}
                      </div>
                    </div>

                    <button className="text-sky-500 font-semibold text-sm">
                      Didn't receive code? Resend
                    </button>
                  </div>

                  <div className="mt-12 space-y-4">
                    <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                      <div className="flex gap-3">
                        <Gift className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-900">Welcome Bonus!</p>
                          <p className="text-sm text-amber-700 mt-1">Earn 500 bonus points after email verification</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-sm">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <p className="text-sm text-gray-600">Your data is encrypted and secure</p>
                    </div>
                  </div>
                </div>

                <button className="w-full bg-sky-500 text-white py-4 px-6 rounded-2xl font-semibold text-lg shadow-lg hover:bg-sky-600 transition-all transform hover:scale-[0.98] mb-8">
                  Verify Email
                </button>
              </div>
            </div>
          </div>
        </div>
          </div>
        </div>
        <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Email Verification</p>
      </div>

      {/* Solution 3: Visual Discovery Path */}
      <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
        <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
          <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
            {/* Status Bar */}
            <div className={components.statusBar.container}>
              <span className={components.statusBar.time}>9:41</span>
              <div className={components.statusBar.battery}>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
                <div className="w-4 h-3 bg-black rounded-sm"></div>
              </div>
            </div>

            {/* Content */}
            <div className="pt-11 h-full bg-white">
              {/* Preferences Selection Screen */}
              <div className="h-full flex flex-col px-6 pt-8">
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">What brings you downtown?</h2>
                    <span className="text-sm font-medium text-sky-500">2 of 3</span>
                  </div>
                  <p className="text-gray-600">Select your favorite categories to personalize your experience</p>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4 pb-6">
                    {categories.map((category) => {
                      const isSelected = selectedCategories.includes(category.id);
                      const Icon = category.icon;
                      return (
                        <button
                          key={category.id}
                          onClick={() => toggleCategory(category.id)}
                          className={`relative p-6 rounded-3xl transition-all transform hover:scale-[0.98] ${
                            isSelected 
                              ? 'bg-gradient-to-br from-sky-400 to-sky-600 shadow-lg' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute top-3 right-3 w-6 h-6 bg-white rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-sky-600" />
                            </div>
                          )}
                          <Icon className={`w-8 h-8 mb-3 ${isSelected ? 'text-white' : 'text-gray-700'}`} />
                          <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                            {category.label}
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-3xl mb-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                        <MapPin className="w-6 h-6 text-orange-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">Enable Location</h3>
                        <p className="text-sm text-gray-600 mb-3">Discover businesses and events near you</p>
                        <button className="bg-orange-500 text-white px-4 py-2 rounded-xl font-medium text-sm">
                          Enable
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <button 
                  className={`w-full py-4 px-6 rounded-2xl font-semibold text-lg shadow-lg transition-all transform hover:scale-[0.98] mb-8 ${
                    selectedCategories.length >= 3 
                      ? 'bg-sky-500 text-white hover:bg-sky-600' 
                      : 'bg-gray-200 text-gray-400'
                  }`}
                  disabled={selectedCategories.length < 3}
                >
                  Continue ({selectedCategories.length}/3 selected)
                </button>
              </div>
            </div>
          </div>
            </div>
          </div>
        </div>
        <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Preference Selection</p>
      </div>
      </div>
    </div>
  );
}

export default SignupOnboarding;